
import React from 'react';
import { Clock, LogOut, ShieldAlert, Phone, CheckCircle, RefreshCw, Mail, TimerReset } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { trackOnboardingStep } from '../services/analyticsService';

const PendingApproval = () => {
  const { user, logout, refreshUser } = useAuth();
  const [isChecking, setIsChecking] = React.useState(false);
  const [statusMessage, setStatusMessage] = React.useState('');

  const submittedAt = React.useMemo(() => {
    const raw = user?.roleChangeRequest?.requestDate || user?.createdAt;
    return raw ? new Date(raw) : null;
  }, [user?.createdAt, user?.roleChangeRequest?.requestDate]);

  const approvalEta = React.useMemo(() => {
    if (!submittedAt || Number.isNaN(submittedAt.getTime())) {
      return 'Usually within 1 working day';
    }

    const elapsedHours = Math.max(0, Math.round((Date.now() - submittedAt.getTime()) / (1000 * 60 * 60)));
    if (elapsedHours < 24) return `Submitted ${elapsedHours || 1} hour${elapsedHours === 1 ? '' : 's'} ago`;

    const elapsedDays = Math.max(1, Math.round(elapsedHours / 24));
    return `Submitted ${elapsedDays} day${elapsedDays === 1 ? '' : 's'} ago`;
  }, [submittedAt]);

  React.useEffect(() => {
    trackOnboardingStep('pending_approval_view');
  }, []);

  const handleCheckStatus = async () => {
    setIsChecking(true);
    setStatusMessage('');
    try {
      await refreshUser();
      setStatusMessage('Status refreshed. If approval is complete, this page will automatically continue.');
    } catch {
      setStatusMessage('Unable to refresh status right now. Please try again shortly.');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4 font-sans">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 text-center relative overflow-hidden animate-in fade-in zoom-in-95">
        <div className="absolute top-0 left-0 w-full h-2 bg-amber-500"></div>
        
        <div className="absolute top-4 right-4 bg-amber-50 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 text-[10px] font-bold px-2.5 py-1 rounded-full border border-amber-100 dark:border-amber-800">
            Stage 2 of 2: Approval
        </div>

        <div className="w-20 h-20 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner mt-4">
          <Clock className="w-10 h-10 animate-pulse" />
        </div>

        <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Pending Manager Approval</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6 leading-relaxed text-sm">
          Your email is successfully verified. You are now in the final stage: Manager Authorization.
          <br/><br/>
          Current Status: <span className="font-bold text-slate-800 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">Pending Assignment</span>
        </p>

        <div className="mb-6 grid grid-cols-1 gap-3 text-left">
          <div className="rounded-xl border border-teal-100 dark:border-teal-900/40 bg-teal-50 dark:bg-teal-900/20 p-4">
            <div className="flex items-center gap-2 text-teal-700 dark:text-teal-300 font-bold text-xs uppercase tracking-wide mb-2">
              <TimerReset className="w-3.5 h-3.5" /> Expected turnaround
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-200">{approvalEta}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">You can safely refresh this page or contact support if this status is older than expected.</p>
          </div>
        </div>

        <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-xl text-xs text-slate-500 dark:text-slate-400 mb-6 text-left border border-slate-100 dark:border-slate-600">
            <p className="font-bold uppercase mb-2 flex items-center gap-2 text-slate-600 dark:text-slate-300">
                <ShieldAlert className="w-3 h-3" /> What happens next?
            </p>
            <ul className="space-y-2 pl-2">
                <li className="flex gap-2 items-start">
                    <span className="mt-0.5 w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0" />
                    <span>Contact your Line Manager or Scheduling Admin to expedite approval.</span>
                </li>
                <li className="flex gap-2 items-start">
                    <span className="mt-0.5 w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0" />
                    <span>Once approved, you will be issued a unique <strong>Employee ID</strong> and <strong>Secure PIN</strong>.</span>
                </li>
                <li className="flex gap-2 items-start">
                    <span className="mt-0.5 w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0" />
                    <span>You will then gain full access to ePRF, Rota, and Asset systems.</span>
                </li>
            </ul>
        </div>

        <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400 text-xs font-bold mb-6">
            <CheckCircle className="w-3 h-3" /> Email Verified
        </div>

        {statusMessage && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl text-xs text-blue-700 dark:text-blue-300 font-semibold">
            {statusMessage}
          </div>
        )}

        <div className="space-y-2 mb-4">
          <button
            onClick={handleCheckStatus}
            disabled={isChecking}
            className="w-full py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-70"
          >
            {isChecking ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Check Approval Status
          </button>
          <a
            href="mailto:support@aspis.software?subject=Approval%20Status%20Request"
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-bold rounded-xl text-sm hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
          >
            <Mail className="w-4 h-4" /> Request manual review
          </a>
          <a
            href="mailto:support@aspis.software?subject=Approval%20Request%20Follow-up"
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-bold rounded-xl text-sm hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
          >
            <Phone className="w-4 h-4" /> Contact Support Team
          </a>
        </div>

        <button 
            onClick={logout}
            className="w-full py-3 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-2 text-sm"
        >
            <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </div>
    </div>
  );
};

export default PendingApproval;
